import { useReducer, useState, useRef } from "react";
import { Button, ThumbsUpSVG, ThumbsDownSVG } from "@newsela/angelou";
import { withTrackEvent, objectTypes, actionNames } from "@newsela/snitch";
import { usePostContentFeedback } from "@/content/useContentFeedback";

import { reactionContainerCss } from "./ReactionButtons.style";
const ButtonWithTrackEvent = withTrackEvent(Button);
import ThumbsUpFilledSVG from "./ThumbsUpFilled.svg";
import ThumbsDownFilledSVG from "./ThumbsDownFilled.svg";

interface ReactionsProps {
  contentId: string;
  targetLanguage: string;
  text: string;
  translationIdentifier: string;
}

type ReactionState = { liked: boolean; disliked: boolean };
type ReactionAction = { type: "LIKE" } | { type: "DISLIKE" } | { type: "UNDO" };

const Reactions = ({
  contentId,
  targetLanguage,
  text,
  translationIdentifier,
}: ReactionsProps) => {
  const likeRef = useRef<HTMLButtonElement | null>(null);
  const dislikeRef = useRef<HTMLButtonElement | null>(null);

  const [hoverItem, setHoverItem] = useState<HTMLButtonElement | null>(null);
  const [postContentFeedback] = usePostContentFeedback();

  const [state, dispatch] = useReducer(
    (_: ReactionState, action: ReactionAction): ReactionState => {
      switch (action.type) {
        case "LIKE":
          return { liked: true, disliked: false };
        case "DISLIKE":
          return { liked: false, disliked: true };
        case "UNDO":
        default:
          return { liked: false, disliked: false };
      }
    },
    { liked: false, disliked: false },
  );

  const handleSubmitReactionFeedback = (
    reaction: "LIKE" | "DISLIKE" | "UNDO",
  ) => {
    postContentFeedback({
      content_id: contentId,
      content_type: "translation",
      extra_data: {
        target_language: targetLanguage,
        text,
        translation_identifier: translationIdentifier,
      },
      reaction:
        reaction === "LIKE"
          ? "thumbs_up"
          : reaction === "DISLIKE"
            ? "thumbs_down"
            : "",
    }).then(({ error }: { error?: Error }) => {
      if (error) {
        console.error("Failed to submit reaction feedback:", error);
      }
    });
  };

  const { liked, disliked } = state;

  const handleReaction = (reactionType: "LIKE" | "DISLIKE" | "UNDO") => {
    dispatch({ type: reactionType });

    if (reactionType !== "UNDO") {
      handleSubmitReactionFeedback(reactionType);
    }
  };

  const buttonProps = {
    flavor: Button.flavors.tertiary,
    objectType: objectTypes.generatedTextAsset,
    actionName: actionNames.select,
    size: Button.sizes.lg,
  };

  return (
    <div css={reactionContainerCss}>
      <ButtonWithTrackEvent
        {...buttonProps}
        //@ts-expect-error for ref type
        ref={likeRef}
        onMouseEnter={() => setHoverItem(likeRef.current)}
        onMouseLeave={() => setHoverItem(null)}
        onClick={() => handleReaction(!liked ? "LIKE" : "UNDO")}
        streamProperties={{
          description: liked
            ? "User removed like from the translation"
            : "User liked the translation",
        }}
        icon={{
          SvgComponent:
            liked || (hoverItem !== null && hoverItem === likeRef.current)
              ? ThumbsUpFilledSVG
              : ThumbsUpSVG,
        }}
        ariaProps={{
          "aria-label": liked ? "Unlike translation" : "Like translation",
        }}
        dataProps={{
          "data-qa-selector": "thumbs_up_reaction",
        }}
      >
        {/* No visible children, icon only button */}
      </ButtonWithTrackEvent>
      <ButtonWithTrackEvent
        {...buttonProps}
        //@ts-expect-error for ref type
        ref={dislikeRef}
        onMouseEnter={() => setHoverItem(dislikeRef.current)}
        onMouseLeave={() => setHoverItem(null)}
        onClick={() => handleReaction(!disliked ? "DISLIKE" : "UNDO")}
        streamProperties={{
          description: disliked
            ? "User removed dislike from the translation"
            : "User disliked the translation",
        }}
        icon={{
          SvgComponent:
            disliked || (hoverItem !== null && hoverItem === dislikeRef.current)
              ? ThumbsDownFilledSVG
              : ThumbsDownSVG,
        }}
        ariaProps={{
          "aria-label": disliked
            ? "undo dislike of translation"
            : "Dislike translation",
        }}
        dataProps={{
          "data-qa-selector": "thumbs_down_reaction",
        }}
      >
        {/* No visible children, icon only button */}
      </ButtonWithTrackEvent>
    </div>
  );
};

export default Reactions;
